/**
 * Per-profile scrape history (PRD §1 "Scrape history", §5.1 #10, §11.1).
 *
 * A paged run log: trigger source, status, queued/started time, duration, items fetched, plus the
 * per-stage detail that makes a partial run readable — scraper, fetch status, map status, attempt
 * count, items, and the error class/message on hover.
 *
 * Stages store the plugin *id*; the display name is resolved from #35 so a run stays readable after
 * a plugin is renamed — and degrades to the raw id when the plugin is no longer installed (§7.1).
 */

import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { RunDTO, RunStatus } from '@lw/shared';
import { listProfileRuns } from '../../api/runs';
import { QK } from '../../api/queryKeys';
import { EmptyState, ErrorState, Modal, Spinner } from '../../components';
import { useScrapersQuery } from '../settings/useScrapers';
import { formatDateTime, formatDuration } from '../../lib/datetime';
import { formatInteger } from '../../lib/format';
import {
  RUN_STATUS_LABEL,
  STAGE_ERROR_CLASS_LABEL,
  STAGE_FETCH_STATUS_LABEL,
  STAGE_MAP_STATUS_LABEL,
  TRIGGER_SOURCE_LABEL,
} from '../../lib/labels';

const PAGE_SIZE = 10;

/** Status-dot class per run status; success/partial/failed dots are defined in home.css. */
const RUN_STATUS_CLASS: Record<RunStatus, string> = {
  queued: 'status--queued',
  running: 'status--running',
  success: 'status--success',
  partial: 'status--partial',
  failed: 'status--failed',
  mapper_error: 'status--mapper_error',
};

function RunCard({
  run,
  timezone,
  pluginName,
}: {
  run: RunDTO;
  timezone: string;
  pluginName: (scraperId: string) => string;
}) {
  return (
    <li className="history-run">
      <div className="history-run__head">
        <span className={`status ${RUN_STATUS_CLASS[run.status]}`}>
          <span className="status__dot" aria-hidden="true" />
          {RUN_STATUS_LABEL[run.status]}
        </span>
        <span className="history-run__when" title={formatDateTime(run.queuedAt, timezone)}>
          {formatDateTime(run.startedAt ?? run.queuedAt, timezone)}
        </span>
        <span className="muted">{TRIGGER_SOURCE_LABEL[run.triggerSource]}</span>
        {run.backfillMonths !== null ? (
          <span className="muted">Backfill · {run.backfillMonths} mo</span>
        ) : null}
        <span className="muted">{formatDuration(run.durationMs)}</span>
        <span className="muted">{formatInteger(run.itemsFetched)} items</span>
      </div>

      {run.errorMessage !== null ? <p className="history-run__error">{run.errorMessage}</p> : null}

      {run.stages.length > 0 ? (
        <div className="history-run__stages">
          <table className="table">
            <thead>
              <tr>
                <th>Scraper</th>
                <th>Fetch</th>
                <th>Map</th>
                <th className="num">Attempts</th>
                <th className="num">Items</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {run.stages.map((stage) => (
                <tr key={`${run.id}-${stage.scraperId}`}>
                  <td title={stage.scraperId}>{pluginName(stage.scraperId)}</td>
                  <td className="cell-muted">{STAGE_FETCH_STATUS_LABEL[stage.fetchStatus]}</td>
                  <td className="cell-muted">{STAGE_MAP_STATUS_LABEL[stage.mapStatus]}</td>
                  <td className="num">{stage.attempts}</td>
                  <td className="num">{formatInteger(stage.items)}</td>
                  <td className="history-run__stage-error" title={stage.errorMessage ?? undefined}>
                    {stage.errorClass !== null ? (
                      <span className="history-run__errclass">
                        {STAGE_ERROR_CLASS_LABEL[stage.errorClass]}
                      </span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                    {stage.errorMessage !== null ? (
                      <span className="history-run__errmsg"> {stage.errorMessage}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted history-run__none">No stage detail recorded for this run.</p>
      )}

      {run.status === 'mapper_error' ? (
        <p className="history-run__note">Raw payload retained; re-map available from Settings.</p>
      ) : null}
    </li>
  );
}

export type ScrapeHistoryDialogProps = {
  open: boolean;
  profileId: number;
  profileName: string;
  timezone: string;
  onClose: () => void;
};

export function ScrapeHistoryDialog({
  open,
  profileId,
  profileName,
  timezone,
  onClose,
}: ScrapeHistoryDialogProps) {
  const [page, setPage] = useState(0);
  const params = { offset: page * PAGE_SIZE, limit: PAGE_SIZE };

  const query = useQuery({
    queryKey: QK.runsPage(profileId, params),
    queryFn: ({ signal }) => listProfileRuns(profileId, params, signal),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const scrapers = useScrapersQuery(open);
  const pluginName = (scraperId: string): string =>
    scrapers.data?.scrapers.find((scraper) => scraper.id === scraperId)?.name ?? scraperId;

  const total = query.data?.total ?? 0;
  const runs = query.data?.runs ?? [];
  const lastPage = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE) - 1;

  return (
    <Modal
      open={open}
      title={`Scrape history — ${profileName}`}
      size="lg"
      flushBody
      onClose={onClose}
      footer={
        <>
          <span className="muted dialog-hint">
            {total === 0 ? 'No runs yet' : `${formatInteger(total)} runs`}
          </span>
          <button
            type="button"
            className="table-action"
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            disabled={page === 0 || query.isFetching}
          >
            <span className="table-action__label">Newer</span>
          </button>
          <button
            type="button"
            className="table-action"
            onClick={() => setPage((current) => Math.min(lastPage, current + 1))}
            disabled={page >= lastPage || query.isFetching}
          >
            <span className="table-action__label">Older</span>
          </button>
        </>
      }
    >
      {query.isPending ? <Spinner size="lg" label="Loading runs…" /> : null}

      {query.isError ? (
        <ErrorState
          title="Couldn't load the run log"
          error={query.error}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      ) : null}

      {query.isSuccess && runs.length === 0 ? (
        <EmptyState
          title="No scrape runs yet"
          description="This profile has never been scraped. Use “Scrape now” from its row menu."
        />
      ) : null}

      {runs.length > 0 ? (
        <ul className="history-runs">
          {runs.map((run) => (
            <RunCard key={run.id} run={run} timezone={timezone} pluginName={pluginName} />
          ))}
        </ul>
      ) : null}
    </Modal>
  );
}

export default ScrapeHistoryDialog;
